import type { Plan, PlanStop } from '@trippath/shared';
import { useTranslation } from 'react-i18next';

import { ModeIcon } from '../icons/ModeIcon';

interface Props {
  plan: Plan;
  /** The stop picked, or the stop whose way on is picked. */
  stop: PlanStop;
  /** Whether its way on is what is picked, not the stop. */
  way: boolean;
  onStop: (stop: PlanStop) => void;
  /** Picks the way on from a stop. */
  onWay: (stop: PlanStop) => void;
}

/**
 * Where what is picked is on the way, as an editor's breadcrumb: the stop or way before it,
 * itself, and the one after, each pressed picked in its place. A way is between its two stops,
 * a stop between the ways in and on.
 */
export function PickedPath({ plan, stop, way, onStop, onWay }: Props) {
  const { t } = useTranslation();
  const at = plan.stops.indexOf(stop);
  const before = plan.stops.slice(Math.max(0, at - 1), at);
  const after = plan.stops.slice(at + 1, at + 2);
  const wayOf = (from: PlanStop, to: PlanStop) =>
    t('way.widget', { from: from.city, to: to.city });
  return (
    <nav className="picked-path" aria-label={t('plan.related')}>
      {way ? (
        <>
          <button
            type="button"
            onClick={() => {
              onStop(stop);
            }}
          >
            ← {stop.city}
          </button>
          <span className="picked-path-here" aria-current="true">
            <ModeIcon mode={stop.out?.mode} size={12} />
            {stop.city} → {after.map((next) => next.city).join('') || '…'}
          </span>
          {after.map((next) => (
            <button
              key={next.id}
              type="button"
              onClick={() => {
                onStop(next);
              }}
            >
              {next.city} →
            </button>
          ))}
        </>
      ) : (
        <>
          {before.map((last) => (
            <button
              key={last.id}
              type="button"
              aria-label={wayOf(last, stop)}
              title={wayOf(last, stop)}
              onClick={() => {
                onWay(last);
              }}
            >
              ← <ModeIcon mode={last.out?.mode} size={12} /> {last.city}
            </button>
          ))}
          <span className="picked-path-here" aria-current="true">
            {stop.city}
          </span>
          {after.map((next) => (
            <button
              key={next.id}
              type="button"
              aria-label={wayOf(stop, next)}
              title={wayOf(stop, next)}
              onClick={() => {
                onWay(stop);
              }}
            >
              {next.city} <ModeIcon mode={stop.out?.mode} size={12} /> →
            </button>
          ))}
        </>
      )}
    </nav>
  );
}
